import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { MapPin, Clock, Euro, Info, ChevronRight, HelpCircle } from 'lucide-react';
import { useContentImage } from '../hooks/useContentImage';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Template for Guide Pages (/visiter-versailles, /que-faire-a-versailles, etc.)
 * Content will be loaded from API based on slug
 * Placeholder shown when content not yet imported
 */
export default function GuidePage() {
  const { slug, lang } = useParams();
  const [guide, setGuide] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const effectiveSlug = slug || 'visiter-versailles';
  const effectiveLang = lang || 'fr';

  // Fetch SEO image for this guide
  const { imageData } = useContentImage('guide', effectiveLang, effectiveSlug);

  React.useEffect(() => {
    fetchGuide();
  }, [effectiveSlug, effectiveLang]);

  const fetchGuide = async () => {
    try {
      const response = await fetch(`${API_URL}/api/guide/${effectiveLang}/${effectiveSlug}`);
      if (response.ok) {
        const data = await response.json();
        setGuide(data);
      }
    } catch (error) {
      console.error('Error fetching guide:', error);
    } finally {
      setLoading(false);
    }
  };

  // Placeholder content for pages not yet imported
  const placeholders = {
    'visiter-versailles': {
      title: 'Visiter Versailles : Guide Complet',
      description: 'Tout savoir pour organiser votre visite à Versailles.',
      status: 'Contenu en cours de rédaction'
    },
    'chateau-de-versailles': {
      title: 'Château de Versailles : Guide Pratique',
      description: 'Découvrez le Château de Versailles, joyau de l\'architecture française.',
      status: 'Contenu en cours de rédaction'
    },
    'jardins-de-versailles': {
      title: 'Jardins de Versailles',
      description: 'Les jardins à la française dessinés par Le Nôtre.',
      status: 'Contenu en cours de rédaction'
    },
    'versailles-en-1-jour': {
      title: 'Versailles en 1 jour depuis Paris',
      description: 'Comment visiter Versailles en une journée.',
      status: 'Contenu en cours de rédaction'
    },
    'que-faire-a-versailles': {
      title: 'Que faire à Versailles ?',
      description: 'Les meilleures activités et visites à Versailles.',
      status: 'Contenu en cours de rédaction'
    }
  };

  const placeholder = placeholders[effectiveSlug] || {
    title: effectiveSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    description: 'Guide touristique de Versailles',
    status: 'Contenu à venir'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
        </div>
        <Footer />
      </div>
    );
  }

  // If guide exists, render full content
  if (guide && guide.content_html) {
    return (
      <div className="min-h-screen bg-stone-50">
        <SEOHead 
          title={guide.seo?.meta_title || guide.title_h1}
          description={guide.seo?.meta_description || guide.chapo}
          canonical={`https://versaillesplus.fr/${effectiveLang !== 'fr' ? effectiveLang + '/' : ''}${effectiveSlug}`}
        />
        
        {/* JSON-LD */}
        {guide.seo?.json_ld && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(guide.seo.json_ld) }} />
        )}
        
        <Header />
        
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm font-inter text-stone-500 mb-6">
            <Link to="/" className="hover:text-red-700">Accueil</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/guide" className="hover:text-red-700">Guide</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-stone-900">{guide.title_h1}</span>
          </nav>

          {/* Hero Image with SEO */}
          {imageData?.has_image && imageData?.image_url && (
            <>
              <Helmet>
                <meta property="og:image" content={imageData.image_url} />
                <meta property="og:image:alt" content={imageData.alt_text || guide.title_h1} />
                <meta name="twitter:image" content={imageData.image_url} />
              </Helmet>
              <figure className="mb-8 rounded-xl overflow-hidden shadow-lg">
                <img
                  src={imageData.image_url}
                  alt={imageData.alt_text || guide.title_h1}
                  title={guide.title_h1}
                  className="w-full h-64 md:h-80 object-cover"
                  loading="eager"
                />
                {imageData.caption && (
                  <figcaption className="bg-stone-100 px-4 py-2 text-sm text-stone-600 italic">
                    {imageData.caption}
                  </figcaption>
                )}
              </figure>
            </>
          )}

          {/* Hero */}
          <header className="mb-8">
            <h1 className="font-playfair text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              {guide.title_h1}
            </h1>
            {guide.chapo && (
              <p className="font-lora text-xl text-stone-600 leading-relaxed">
                {guide.chapo}
              </p>
            )}
          </header>

          {/* Quick Answer Box (for AI/Featured Snippets) */}
          {guide.direct_answer && (
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-8">
              <p className="font-inter text-stone-800">{guide.direct_answer}</p>
            </div>
          )}

          {/* Main Content */}
          <article 
            className="prose prose-stone prose-lg max-w-none mb-12"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(guide.content_html || '') }}
          />

          {/* Practical Info Box */}
          {guide.practical_info && (
            <div className="bg-white rounded-lg border border-stone-200 p-6 mb-8">
              <h2 className="font-playfair text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                <Info className="h-5 w-5 text-red-700" />
                Informations pratiques
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-inter text-sm">
                {guide.practical_info.hours && (
                  <div className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-stone-500 mt-1" />
                    <div>
                      <span className="font-medium">Horaires</span>
                      <p className="text-stone-600">{guide.practical_info.hours}</p>
                    </div>
                  </div>
                )}
                {guide.practical_info.price && (
                  <div className="flex items-start gap-2">
                    <Euro className="h-4 w-4 text-stone-500 mt-1" />
                    <div>
                      <span className="font-medium">Tarifs</span>
                      <p className="text-stone-600">{guide.practical_info.price}</p>
                    </div>
                  </div>
                )}
                {guide.practical_info.address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-stone-500 mt-1" />
                    <div>
                      <span className="font-medium">Adresse</span>
                      <p className="text-stone-600">{guide.practical_info.address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {guide.faq && guide.faq.length > 0 && (
            <section className="mb-12">
              <h2 className="font-playfair text-2xl font-bold text-stone-900 mb-6 flex items-center gap-2">
                <HelpCircle className="h-6 w-6 text-red-700" />
                Questions fréquentes
              </h2>
              <div className="space-y-4">
                {guide.faq.map((item, idx) => (
                  <details key={idx} className="bg-white rounded-lg border border-stone-200 p-4 group">
                    <summary className="font-inter font-medium text-stone-900 cursor-pointer list-none flex items-center justify-between">
                      {item.question}
                      <ChevronRight className="h-5 w-5 text-stone-400 group-open:rotate-90 transition-transform" />
                    </summary>
                    <p className="mt-3 font-inter text-stone-600 pl-0">{item.answer}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Related Links */}
          {guide.internal_links_out && guide.internal_links_out.length > 0 && (
            <section className="border-t border-stone-200 pt-8">
              <h2 className="font-playfair text-lg font-bold text-stone-900 mb-4">À lire aussi</h2>
              <div className="flex flex-wrap gap-2">
                {guide.internal_links_out.map((link, idx) => (
                  <Link 
                    key={idx}
                    to={link.url}
                    className="px-4 py-2 bg-stone-100 rounded-full text-sm font-inter text-stone-700 hover:bg-red-50 hover:text-red-700 transition-colors"
                  >
                    {link.anchor}
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
        
        <Footer />
      </div>
    );
  }

  // Placeholder template for pages not yet imported
  return (
    <div className="min-h-screen bg-stone-50">
      <SEOHead 
        title={`${placeholder.title} | VersaillesPlus`}
        description={placeholder.description}
      />
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm font-inter text-stone-500 mb-6">
          <Link to="/" className="hover:text-red-700">Accueil</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/guide" className="hover:text-red-700">Guide</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-stone-900">{placeholder.title}</span>
        </nav>

        {/* Placeholder Hero */}
        <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-lg p-8 md:p-12 text-white mb-8">
          <h1 className="font-playfair text-3xl md:text-4xl font-bold mb-4">
            {placeholder.title}
          </h1>
          <p className="font-lora text-lg text-stone-300 mb-6">
            {placeholder.description}
          </p>
          <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/40 rounded-lg px-4 py-2 text-amber-200 text-sm">
            <Info className="h-4 w-4" />
            {placeholder.status}
          </div>
        </div>

        {/* Coming Soon Info */}
        <div className="bg-white rounded-lg border border-stone-200 p-8 text-center">
          <h2 className="font-playfair text-xl font-bold text-stone-900 mb-3">
            Guide en préparation
          </h2>
          <p className="font-inter text-stone-600 mb-6 max-w-lg mx-auto">
            Ce guide complet est actuellement en cours de rédaction par notre équipe éditoriale. 
            Il sera bientôt disponible avec toutes les informations pratiques pour votre visite.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link 
              to="/archives" 
              className="px-4 py-2 bg-red-700 text-white rounded-lg font-inter text-sm hover:bg-red-800 transition-colors"
            >
              Consulter les archives
            </Link>
            <Link 
              to="/numeros" 
              className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg font-inter text-sm hover:bg-stone-200 transition-colors"
            >
              Voir les numéros PDF
            </Link>
          </div>
        </div>

        {/* Related Articles from Archives */}
        <section className="mt-12">
          <h2 className="font-playfair text-xl font-bold text-stone-900 mb-4">
            Articles liés dans nos archives
          </h2>
          <p className="font-inter text-stone-600 text-sm mb-4">
            En attendant le guide complet, découvrez nos articles d'archives sur ce sujet.
          </p>
          <Link 
            to={`/categorie/histoire`}
            className="inline-flex items-center gap-2 text-red-700 hover:text-red-800 font-inter text-sm"
          >
            Voir les articles Histoire
            <ChevronRight className="h-4 w-4" />
          </Link>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
